import { Tooltip as CkTooltip, Portal } from '@chakra-ui/react';
import * as React from 'react';

type RootProps = React.ComponentProps<typeof CkTooltip.Root>;
type ContentProps = React.ComponentProps<typeof CkTooltip.Content>;

type TooltipProps = Omit<RootProps, 'children'> & {
    children: React.ReactElement;
    disabled?: boolean;
    portalled?: boolean;
    content?: React.ReactNode;
    contentProps?: ContentProps;
    portalRef?: React.RefObject<HTMLElement> | null;
    showArrow?: boolean;
};

export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
    function Tooltip(
        {
            showArrow,
            children,
            disabled,
            portalled = true,
            content,
            contentProps,
            portalRef,
            ...rest
        },
        ref,
    ) {
        // `disabled` forwards into the machine (which then never opens)
        // instead of early-returning bare children: the tree stays stable
        // when a host toggles it (the sidebar's mini rail does per toggle),
        // so CSS transitions on the trigger subtree survive. Trigger is
        // asChild — the wrapper adds no DOM either way.
        return (
            <CkTooltip.Root disabled={disabled} {...rest}>
                <CkTooltip.Trigger asChild>{children}</CkTooltip.Trigger>

                <Portal
                    disabled={!portalled}
                    container={portalRef ?? undefined}
                >
                    <CkTooltip.Positioner>
                        <CkTooltip.Content ref={ref} {...contentProps}>
                            {showArrow && (
                                <CkTooltip.Arrow>
                                    <CkTooltip.ArrowTip />
                                </CkTooltip.Arrow>
                            )}
                            {content}
                        </CkTooltip.Content>
                    </CkTooltip.Positioner>
                </Portal>
            </CkTooltip.Root>
        );
    },
);

Tooltip.displayName = 'Tooltip';
